import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Slide,
  TextField,
  IconButton,
  FormControl,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadDenominationsList,
  loadEquipmentTypesList
} from 'src/store/actions/catalogActions';
import {
  getAddressByZipCode,
  LOAD_ADDRESS_BY_ZIP_CODE
} from 'src/store/actions/clientActions';
import DropdownMenu from 'src/components/DropdownMenu/DropdownMenu';
import {
  LOAD_QUOTE_CATEGORIES,
  loadQuoteCategoriesList
} from 'src/store/actions/quoteActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { PersonType } from 'src/utils/constants/clients';
import { CREATE_DISTRIBUTOR } from 'src/store/actions/distributorActions';
import Loading from 'src/components/Loading';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from './styles';
import DistributorAlert from './DistributorAlert';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PersonTypeOptions = [
  {
    key: PersonType.PHYSICAL,
    name: 'ENUMS.PERSON_TYPE.PHYSICAL'
  },
  {
    key: PersonType.LEGAL,
    name: 'ENUMS.PERSON_TYPE.LEGAL'
  }
];

const DistributorForm = ({ open, handleClose, submit }) => {
  const [denominations, setDenominations] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [addressData, setAddressData] = useState({
    cities: [],
    states: [],
    isValid: null
  });
  const [categoryList, setCategoryList] = useState([]);
  const isLoadingAddress = useSelector(
    isLoadingSelector([LOAD_ADDRESS_BY_ZIP_CODE.action])
  );
  const isLoadingCreateDistributor = useSelector(
    isLoadingSelector([CREATE_DISTRIBUTOR.action])
  );

  const { classes } = useStyles();
  const isLoadingCategoriesList = useSelector(
    isLoadingSelector([LOAD_QUOTE_CATEGORIES.action])
  );
  const dispatch = useDispatch();
  const initialState = makeInitialState();
  const resetRef = useRef();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch(loadDenominationsList(setDenominations));
      dispatch(loadEquipmentTypesList(setEquipmentTypes));
      dispatch(loadQuoteCategoriesList(setCategoryList));
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [dispatch, open]);

  const handleZipCodeChange = useCallback(
    (e) => {
      const { value } = e.target;

      if (value.length < 5) {
        setAddressData((prev) => ({
          ...prev,
          isValid: null
        }));
      }

      if (value.length === 5) {
        dispatch(getAddressByZipCode(value, setAddressData));
      }
    },
    [dispatch]
  );

  const handleCreateDistributor = useCallback(
    (values, { resetForm }) => {
      submit({
        ...values
      });
      resetForm(initialState);
    },
    [submit, initialState]
  );

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  if (!open) {
    return null;
  }

  const canSubmit = addressData.isValid === true;

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.root }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <DialogTitle className={classes.header} id="distributor-form">
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            handleClose();
          }}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {t('DISTRIBUTORS_VIEW.CREATE_DISTRIBUTOR')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ marginBottom: '0.5rem' }}>
        {isLoadingCreateDistributor ? (
          <Loading />
        ) : (
          <Formik
            initialValues={initialState}
            validationSchema={validationSchema}
            onSubmit={handleCreateDistributor}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              resetForm,
              setFieldValue
            }) => {
              resetRef.current = () => resetForm();
              const showCopomexError =
                addressData.isValid === false && values.zipCode?.length === 5;
              return (
                <form onSubmit={handleSubmit}>
                  <DistributorAlert
                    denominations={denominations}
                    equipmentTypes={equipmentTypes}
                    categoryList={categoryList}
                    loading={isLoadingCategoriesList}
                  />
                  <Grid container direction="column" alignItems="stretch">
                    <Grid item>
                      <DropdownMenu
                        list={PersonTypeOptions}
                        value={values.personType}
                        setValue={(value) => setFieldValue('personType', value)}
                        label={t('COMMON.PERSON_TYPE')}
                        error={Boolean(touched.personType && errors.personType)}
                        errorText={touched.personType && t(errors.personType)}
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        InputProps={{
                          autoComplete: 'off'
                        }}
                        error={Boolean(touched.name && errors.name)}
                        fullWidth
                        helperText={touched.name && t(errors.name)}
                        label={t('COMMON.NAME')}
                        margin="normal"
                        name="name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.name}
                        variant="outlined"
                      />
                    </Grid>
                    {values.personType === PersonType.LEGAL && (
                      <Grid item>
                        <FormControl
                          fullWidth
                          error={Boolean(
                            touched.denomination && errors.denomination
                          )}
                        >
                          <Autocomplete
                            id="denomination"
                            options={denominations}
                            loading={isLoadingCategoriesList}
                            getOptionLabel={(option) =>
                              option?.name ? `${option.name}` : ''
                            }
                            value={
                              denominations?.find(
                                (denomination) =>
                                  denomination.name === values.denomination
                              ) ?? null
                            }
                            onChange={(_e, value) => {
                              setFieldValue(
                                'denomination',
                                value ? value.name : ''
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('COMMON.DENOMINATION')}
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                helperText={
                                  touched.denomination && t(errors.denomination)
                                }
                                error={Boolean(
                                  touched.denomination && errors.denomination
                                )}
                              />
                            )}
                            loadingText={t('COMMON.LOADING')}
                          />
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item>
                      <TextField
                        InputProps={{
                          autoComplete: 'off'
                        }}
                        error={Boolean(
                          touched.commercialName && errors.commercialName
                        )}
                        fullWidth
                        helperText={
                          touched.commercialName && t(errors.commercialName)
                        }
                        label={t('COMMON.COMERCIAL_NAME')}
                        margin="normal"
                        name="commercialName"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.commercialName}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item>
                      <FormControl
                        fullWidth
                        error={Boolean(
                          touched.equipmentTypeId && errors.equipmentTypeId
                        )}
                      >
                        <Autocomplete
                          id="equipmentTypeId"
                          options={equipmentTypes}
                          loading={isLoadingCategoriesList}
                          getOptionLabel={(option) =>
                            option.name ? `${option.name}` : ''
                          }
                          value={
                            equipmentTypes?.find(
                              (equipment) =>
                                equipment.id === values.equipmentTypeId
                            ) ?? null
                          }
                          onChange={(_e, value) => {
                            setFieldValue('equipmentTypeId', value?.id ?? '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('COMMON.EQUIPMENT_TYPE')}
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              helperText={
                                touched.equipmentTypeId &&
                                t(errors.equipmentTypeId)
                              }
                              error={Boolean(
                                touched.equipmentTypeId &&
                                errors.equipmentTypeId
                              )}
                            />
                          )}
                          loadingText={t('COMMON.LOADING')}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item>
                      <TextField
                        InputProps={{
                          autoComplete: 'off'
                        }}
                        error={Boolean(touched.rfc && errors.rfc)}
                        fullWidth
                        helperText={touched.rfc && t(errors.rfc)}
                        label={t('COMMON.RFC')}
                        margin="normal"
                        name="rfc"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="text"
                        value={values.rfc}
                        variant="outlined"
                      />
                    </Grid>

                    <FormControl
                      fullWidth
                      error={Boolean(
                        touched.quoteCategory && errors.quoteCategory
                      )}
                    >
                      <Autocomplete
                        id="quoteCategory"
                        options={categoryList}
                        loading={isLoadingCategoriesList}
                        getOptionLabel={(option) =>
                          option.name ? `${option.name}` : ''
                        }
                        onChange={(_e, value) => {
                          setFieldValue('quoteCategory', value?.id ?? '');
                          setFieldValue('quoteCategoryId', value?.id ?? '');
                        }}
                        value={
                          categoryList?.find(
                            (category) => category.id === values.quoteCategory
                          ) ?? null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('DISTRIBUTORS_VIEW.PARAMETERS.CATEGORY')}
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            helperText={
                              touched.quoteCategory && t(errors.quoteCategory)
                            }
                            error={Boolean(
                              touched.quoteCategory && errors.quoteCategory
                            )}
                          />
                        )}
                        loadingText={t('COMMON.LOADING')}
                      />
                    </FormControl>

                    <Grid item>
                      <TextField
                        error={Boolean(
                          (touched.zipCode && errors.zipCode) ||
                          showCopomexError
                        )}
                        fullWidth
                        helperText={
                          showCopomexError
                            ? t('ERRORS.ZIP_CODE_WAS_NOT_FOUND')
                            : touched.zipCode && t(errors.zipCode)
                        }
                        label={t('COMMON.ZIP_CODE')}
                        inputProps={{ maxLength: 5, autoComplete: 'off' }}
                        margin="normal"
                        name="zipCode"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleZipCodeChange(e);
                          handleChange(e);
                        }}
                        type="text"
                        value={values.zipCode}
                        variant="outlined"
                      />
                    </Grid>

                    <FormControl
                      fullWidth
                      error={Boolean(touched.city && errors.city)}
                    >
                      <Autocomplete
                        id="city-autocomplete"
                        options={addressData.cities.map((city) => ({
                          key: city,
                          name: city
                        }))}
                        getOptionLabel={(option) => {
                          return option?.name ? `${option.name}` : '';
                        }}
                        onChange={(e, value) => {
                          setFieldValue(
                            'city',
                            value ? value.name : e.target.value
                          );
                        }}
                        value={
                          addressData?.cities
                            .map((city) => ({
                              key: city,
                              name: city
                            }))
                            .find((city) => city.name === values.city) ?? {
                            key: values.city ?? '',
                            name: values.city ?? ''
                          }
                        }
                        loading={isLoadingAddress}
                        clearOnBlur
                        selectOnFocus={false}
                        handleHomeEndKeys={false}
                        freeSolo
                        inputValue={values.city ?? ''}
                        onInputChange={(_, value) => {
                          setFieldValue('city', value);
                        }}
                        sx={{ marginBottom: 2, marginTop: 2 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              'data-testid': 'city-autocomplete-input'
                            }}
                            label={t('COMMON.CITY')}
                            variant="outlined"
                            fullWidth
                            helperText={touched.city && t(errors.city)}
                            error={Boolean(touched.city && errors.city)}
                          />
                        )}
                        loadingText={t('COMMON.LOADING')}
                      />
                    </FormControl>

                    <FormControl
                      fullWidth
                      error={Boolean(touched.state && errors.state)}
                    >
                      <Autocomplete
                        options={addressData.states.map((state) => ({
                          key: state,
                          name: state
                        }))}
                        getOptionLabel={(option) =>
                          option?.name ? `${option.name}` : ''
                        }
                        onChange={(e, value) => {
                          setFieldValue(
                            'state',
                            value ? value.name : e.target.value
                          );
                        }}
                        value={
                          addressData?.states
                            .map((state) => ({
                              key: state,
                              name: state
                            }))
                            .find((state) => state.name === values.state) ?? {
                            key: values.state ?? '',
                            name: values.state ?? ''
                          }
                        }
                        loading={isLoadingAddress}
                        clearOnBlur
                        selectOnFocus={false}
                        handleHomeEndKeys={false}
                        freeSolo
                        inputValue={values.state ?? ''}
                        onInputChange={(_, value) => {
                          setFieldValue('state', value);
                        }}
                        sx={{ marginBottom: 2, marginTop: 2 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            fullWidth
                            label={t('COMMON.STATE')}
                            error={Boolean(touched.state && errors.state)}
                            helperText={touched.state && t(errors.state)}
                          />
                        )}
                        loadingText={t('COMMON.LOADING')}
                      />
                    </FormControl>

                    <Grid item className={classes.buttons}>
                      <Button
                        className={clsx(classes.label, classes.buttons)}
                        type="submit"
                        color="primary"
                        variant="outlined"
                        disabled={!canSubmit}
                      >
                        {t('COMMON.CREATE')}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

DistributorForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired
};

export default DistributorForm;
